import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { alertApi } from '../lib/api';
import { PageHeader } from '../components/ui';
import SignificanceBadge from '../components/fld/SignificanceBadge';
import AlertCodeMapModal from '../components/alert/AlertCodeMapModal';
import AlertLoanDistributionSection from '../components/alert/AlertLoanDistributionSection';

// 'NA' = no disparity toward the PBG (PBG paid the same or less) — favorable, per the
// HMDA Fair-Lending Dashboard convention.
type RiskLevel = 'NA' | 'LOW' | 'MEDIUM' | 'HIGH';

interface RateDisparityResult {
  category: 'sex' | 'ethnicity';
  regulatorRule: 'FRB' | 'FDIC';
  group: string;
  controlGroup: string;
  groupCount: number;
  controlCount: number;
  groupMeanRate: number;
  controlMeanRate: number;
  meanDifferenceBps: number;
  confidenceInterval: { lower: number; upper: number };
  pValue: number | null;
  testUsed: 't-test' | 'insufficient-data';
  isStatisticallySignificant: boolean;
  sampleSizeWarning: boolean;
  riskLevel: RiskLevel;
  denominator: { transparencyNote: string };
  sexBlocked: boolean;
  blockedReason: string | null;
}

interface StratumResult extends RateDisparityResult {
  stratumDimension: 'collateral' | 'type' | 'creditBand' | 'callReportCode' | 'product';
  stratum: string;
}

// Matches PRODUCT_STRATUM_DELIM in analyzeConsumerRateDisparity.ts — the 'product' stratum key
// is `<subcode> ⟫ <collateral> ⟫ <type>`; split on this to rebuild the nested tree.
const PRODUCT_DELIM = ' ⟫ ';

const RISK_STYLE: Record<RiskLevel, string> = {
  NA: 'bg-gray-100 text-gray-600',
  LOW: 'bg-green-100 text-green-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  HIGH: 'bg-red-100 text-red-700',
};

// Group the per-reason quarantine counts into human buckets so "1,215 quarantined" reads
// honestly — an unclassifiable loan type is very different from a valid loan whose name
// simply isn't in the 1996 proxy list.
const QUARANTINE_LABEL: Record<string, string> = {
  BLANK_CODE: 'Unclassified loan type',
  MISSING_AMOUNT_ON_THRESHOLD_CODE: 'Unclassified loan type',
  CLASSIFIER_UNKNOWN: 'Unclassified loan type',
  SEX_UNKNOWN_NAME: 'Sex not resolvable (name not in list)',
  SEX_NOT_IN_LIST: 'Sex not resolvable (name not in list)',
  ETHNICITY_INDETERMINATE: 'Ethnicity indeterminate',
  GEOCODE_NO_MATCH: 'No geocode match',
  FRB_BLANK_COMAKER: 'Co-maker blank',
};

// Map a Welch p-value to the same significance bands FLD/HMDA show, for a simpler, consistent
// display (<1% / <5% / <10% / >10%) instead of a raw p-value.
//
// Direction-aware per Michael/Wallace (2026-06-03): significance is only meaningful for an
// ADVERSE disparity (PBG pays more). When the PBG pays the same or LESS there is no disparity
// toward the group, so significance is NA — never a band. When the disparity IS adverse but we
// couldn't establish significance (small/insufficient sample), show ">10%" (not significant),
// not NA. NA is reserved strictly for the favorable/no-disparity direction.
function pBand(
  pValue: number | null,
  testUsed: 't-test' | 'insufficient-data',
  meanDifferenceBps: number,
): '<1%' | '<5%' | '<10%' | '>10%' | 'NA' {
  if (meanDifferenceBps <= 0) return 'NA'; // favorable — no disparity toward the PBG
  if (testUsed === 'insufficient-data' || pValue == null) return '>10%'; // adverse, not significant
  if (pValue < 0.01) return '<1%';
  if (pValue < 0.05) return '<5%';
  if (pValue < 0.1) return '<10%';
  return '>10%';
}

// ── Summary of Risks (mirrors the HMDA/FLD summary) ──────────────────────────
interface RiskRow {
  category: 'sex' | 'ethnicity';
  pbg: string; // FEMALE | HISPANIC (prohibited-basis group)
  bps: number; // positive — the group pays MORE (adverse)
  product: string; // population / product context
  band: '<1%' | '<5%' | '<10%';
  volume: number;
  regulator: string;
}

function riskProductLabel(r: StratumResult): string {
  switch (r.stratumDimension) {
    case 'callReportCode': return r.stratum;
    case 'product': return r.stratum.split(PRODUCT_DELIM).join(' › ');
    default: return r.stratum;
  }
}

function actionFor(band: RiskRow['band']): string {
  if (band === '<1%') return 'Targeted Review';
  if (band === '<5%') return 'Focal Point';
  return 'Monitor';
}

/**
 * Collect the statistically significant ADVERSE disparities (group pays more, band <10%) into
 * summary rows — the headline "what an examiner would flag" view. Top-level (whole consumer
 * population) + the consumer-meaningful breakdowns (subcode + like-product); the generic
 * collateral/type/credit-band strata still persist as findings but aren't repeated here.
 */
function collectRisks(results: RateDisparityResult[], stratified: StratumResult[]): RiskRow[] {
  const rows: RiskRow[] = [];
  const add = (r: RateDisparityResult, product: string, band: string) => {
    if (r.sexBlocked || r.riskLevel === 'LOW' || r.meanDifferenceBps <= 0) return;
    if (band !== '<1%' && band !== '<5%' && band !== '<10%') return;
    rows.push({
      category: r.category, pbg: r.group, bps: r.meanDifferenceBps, product,
      band, volume: r.groupCount, regulator: r.regulatorRule,
    });
  };
  for (const r of results) add(r, 'All consumer (6-series)', pBand(r.pValue, r.testUsed, r.meanDifferenceBps));
  for (const r of stratified) {
    if (r.stratumDimension === 'callReportCode' || r.stratumDimension === 'product') {
      add(r, riskProductLabel(r), pBand(r.pValue, r.testUsed, r.meanDifferenceBps));
    }
  }
  const rank: Record<string, number> = { '<1%': 0, '<5%': 1, '<10%': 2 };
  return rows.sort((a, b) => rank[a.band] - rank[b.band] || b.bps - a.bps);
}

function bucketQuarantine(
  breakdown: { reason: string; count: number }[],
): { label: string; count: number }[] {
  const out = new Map<string, number>();
  for (const { reason, count } of breakdown) {
    const label = QUARANTINE_LABEL[reason] ?? 'Other';
    out.set(label, (out.get(label) ?? 0) + count);
  }
  return [...out.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-bold text-navy-900">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function ResultCard({ r }: { r: RateDisparityResult }) {
  if (r.sexBlocked) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {r.group} vs {r.controlGroup} ({r.regulatorRule}) — sex analysis blocked
            </p>
            <p className="text-sm text-amber-800 mt-1">{r.blockedReason}</p>
          </div>
        </div>
      </div>
    );
  }

  const more = r.meanDifferenceBps > 0;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-navy-900">
          {r.group} <span className="text-gray-400 font-normal">vs {r.controlGroup}</span>{' '}
          <span className="text-xs text-gray-400 font-mono">{r.regulatorRule}</span>
        </p>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${RISK_STYLE[r.riskLevel]}`}>
          {r.riskLevel}
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <span className={`text-3xl font-bold ${more ? 'text-red-600' : 'text-green-700'}`}>
          {more ? '+' : ''}{r.meanDifferenceBps.toFixed(0)} bps
        </span>
        <span className="text-sm text-gray-500">
          {more ? `${r.group} pays more` : `${r.group} pays less`}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <span className="text-gray-500">{r.group} mean</span>
        <span className="text-right font-medium">{(r.groupMeanRate * 100).toFixed(3)}% <span className="text-gray-400">(n={r.groupCount.toLocaleString()})</span></span>
        <span className="text-gray-500">{r.controlGroup} mean</span>
        <span className="text-right font-medium">{(r.controlMeanRate * 100).toFixed(3)}% <span className="text-gray-400">(n={r.controlCount.toLocaleString()})</span></span>
        <span className="text-gray-500">Significance</span>
        <span className="text-right">
          <SignificanceBadge level={pBand(r.pValue, r.testUsed, r.meanDifferenceBps)} />
        </span>
      </div>

      {r.sampleSizeWarning && (
        <p className="mt-2 text-xs text-amber-600">Small sample — interpret with caution.</p>
      )}
      <p className="mt-2 text-xs text-gray-400">{r.denominator.transparencyNote}</p>

      {/* Statistical detail — hidden by default to keep the headline clean; click to drill in.
          The data (CI, test) is still computed + persisted; this just controls display. */}
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-ocean-600 hover:text-ocean-700 select-none">
          Statistical detail
        </summary>
        <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
          <span>Exact p-value</span>
          <span className="text-right font-medium">
            {r.testUsed === 'insufficient-data' || r.pValue == null ? 'N/A' : r.pValue.toFixed(4)}
          </span>
          <span>95% confidence interval</span>
          <span className="text-right font-medium">
            [{r.confidenceInterval.lower.toFixed(0)}, {r.confidenceInterval.upper.toFixed(0)}] bps
          </span>
          <span>Test</span>
          <span className="text-right font-medium">
            {r.testUsed === 't-test' ? "Welch's t-test" : 'Insufficient data'}
          </span>
        </div>
      </details>
    </div>
  );
}

export default function AlertFileReportPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: fileData, isLoading: fileLoading } = useQuery({
    queryKey: ['alert-file', id],
    queryFn: async () => (await alertApi.getFile(id)).data,
    enabled: !!id,
  });
  const file = fileData?.file;
  const quarantineBuckets = bucketQuarantine(fileData?.quarantineBreakdown ?? []);

  const { data: analysisData, isLoading: analysisLoading } = useQuery({
    queryKey: ['alert-analysis', id],
    queryFn: async () => (await alertApi.getConsumerRateDisparity(id)).data,
    enabled: !!id,
  });
  const analysis = analysisData?.analysis;
  // Collateral code -> bank description, for DISPLAY only (the analysis still groups on
  // the raw code; this just relabels the cards). From the file's appended "COLLATERAL".
  const collateralDecodes: Record<string, string> = analysisData?.collateralDecodes ?? {};
  const decodeColl = (code: string) => collateralDecodes[code] || code;
  const typeDecodes: Record<string, string> = analysisData?.typeDecodes ?? {};
  const decodeType = (code: string) => typeDecodes[code] || code;

  // Codes in this file the team's map doesn't decode yet — drives the "manage codes" prompt.
  const [codeMapOpen, setCodeMapOpen] = useState(false);
  const { data: unmappedData } = useQuery({
    queryKey: ['alert-unmapped', id],
    queryFn: async () => (await alertApi.getUnmappedCodes(id)).data as {
      unmapped: Record<'COLLATERAL' | 'TYPE' | 'NOTE_PURPOSE', string[]>;
      totalUnmapped: number;
    },
    enabled: !!id,
  });
  const totalUnmapped = unmappedData?.totalUnmapped ?? 0;
  const prefillCodes = unmappedData
    ? (['COLLATERAL', 'TYPE', 'NOTE_PURPOSE'] as const).flatMap((t) =>
        (unmappedData.unmapped[t] ?? []).map((code) => ({ codeType: t, code })),
      )
    : [];

  const runMutation = useMutation({
    mutationFn: async () => alertApi.runConsumerRateDisparity(id),
    onSuccess: () => {
      toast.success('Consumer rate-disparity analysis complete.');
      queryClient.invalidateQueries({ queryKey: ['alert-analysis', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Analysis failed');
    },
  });

  // Borrowers-only override of the #7 co-maker gate. A person decides it (the gate cannot tell "no
  // co-borrowers" from "not captured"), and the analysis re-runs so the results match the setting.
  const borrowerOnlyMutation = useMutation({
    mutationFn: async (enabled: boolean) => alertApi.setSexBorrowerOnly(id, enabled),
    onSuccess: (_res, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['alert-file', id] });
      toast.success(enabled ? 'Sex analysis will run on primary borrowers only.' : 'Borrowers-only sex analysis turned off.');
      runMutation.mutate();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Could not change the sex analysis setting');
    },
  });
  const setBorrowerOnly = (enabled: boolean) => {
    const ok = enabled
      ? window.confirm(
          'Run the sex analysis on primary borrowers only?\n\nUse this only when the institution confirms its consumer ' +
            'loans have no co-borrowers. Joint applications cannot be identified, and the results and exports will say ' +
            'the analysis was run on primary borrowers only.',
        )
      : window.confirm('Turn off borrowers-only? The sex analysis will be blocked again until co-maker data is provided.');
    if (ok) borrowerOnlyMutation.mutate(enabled);
  };

  // Download the standalone examiner-style export of the analysis (Excel or PDF).
  const exportMutation = useMutation({
    mutationFn: async (format: 'xlsx' | 'pdf') => {
      const res = await alertApi.exportAnalysis(id, format);
      const type =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type });
      const cd = (res.headers?.['content-disposition'] as string | undefined) || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const name = match?.[1] || `${(file?.fileName || 'ALERT').replace(/\.[^.]+$/, '')}_ALERT_Analysis.${format}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: () => toast.error('Export failed. Run the analysis first, then try again.'),
  });
  const exportingFormat = exportMutation.isPending ? exportMutation.variables : null;

  const results: RateDisparityResult[] = analysis?.resultJson?.results ?? [];
  const stratified: StratumResult[] = analysis?.resultJson?.stratifiedResults ?? [];
  const stratifiedCount = stratified.length;
  const sexResults = results.filter((r) => r.category === 'sex');
  const ethResults = results.filter((r) => r.category === 'ethnicity');

  // Group the call-report-code (6a/6b/6c/6d) stratified results by subcode for the breakout.
  const subcodeGroups = (() => {
    const m = new Map<string, StratumResult[]>();
    for (const r of stratified) {
      if (r.stratumDimension !== 'callReportCode') continue;
      const bucket = m.get(r.stratum);
      if (bucket) bucket.push(r);
      else m.set(r.stratum, [r]);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  })();

  // Nested "like product" tree: subcode -> collateral code -> loan type -> results[].
  // Built from the 'product' stratum (only combinations with data are present).
  const productTree = (() => {
    const tree = new Map<string, Map<string, Map<string, StratumResult[]>>>();
    for (const r of stratified) {
      if (r.stratumDimension !== 'product') continue;
      const [subcode = '?', coll = 'UNKNOWN', typ = 'UNKNOWN'] = r.stratum.split(PRODUCT_DELIM);
      if (!tree.has(subcode)) tree.set(subcode, new Map());
      const collMap = tree.get(subcode)!;
      if (!collMap.has(coll)) collMap.set(coll, new Map());
      const typeMap = collMap.get(coll)!;
      const bucket = typeMap.get(typ);
      if (bucket) bucket.push(r);
      else typeMap.set(typ, [r]);
    }
    return tree;
  })();
  const sortedEntries = <V,>(m: Map<string, V>): [string, V][] =>
    [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  const riskRows = analysis ? collectRisks(results, stratified) : [];

  if (fileLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-gray-400">
        <ArrowPathIcon className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!file) {
    return (
      <div className="text-center py-24">
        <p className="text-gray-500">ALERT file not found.</p>
        <button onClick={() => navigate('/alert/upload')} className="mt-4 text-ocean-600 hover:text-ocean-700 font-medium">
          &larr; Back to upload
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => navigate('/alert/upload')}
        className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4 transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" /> ALERT files
      </button>

      <PageHeader
        title={file.fileName}
        description={`${file.regulator} proxy · run window ${new Date(file.periodStart).toLocaleDateString('en-US', { timeZone: 'UTC' })} – ${new Date(file.periodEnd).toLocaleDateString('en-US', { timeZone: 'UTC' })}`}
      />

      {/* Rollups */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Stat label="Total loans" value={file.totalLoans} sub={`${file.inPeriodLoans?.toLocaleString()} in window`} />
        <Stat label="Consumer" value={file.consumerLoans} sub="6-series" />
        <Stat label="Small business" value={file.smallBusinessLoans} />
        <Stat label="Small farm" value={file.smallFarmLoans} />
        <Stat label="Residential" value={file.residentialLoans} />
        <Stat label="Quarantined" value={file.quarantinedLoans} sub="set aside — see breakdown" />
      </div>

      {/* Quarantine breakdown — why loans were set aside (honest split of the single KPI) */}
      {quarantineBuckets.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Quarantined — by reason
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {quarantineBuckets.map((b) => (
              <span key={b.label} className="text-gray-700">
                <span className="font-semibold text-navy-900">{b.count.toLocaleString()}</span> {b.label}
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Quarantined loans are <span className="font-medium">not dropped wholesale</span> — they're excluded
            per category with a transparent denominator. A loan whose sex can't be proxied still counts toward
            ethnicity, and vice-versa. "Name not in list" is expected (the FRB list is from 1996) and is not a data error.
          </p>
        </div>
      )}

      {/* Co-maker sex gate */}
      {file.sexAnalysisBlocked && !file.sexBorrowerOnly && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Sex analysis blocked (#7 file-level gate)</p>
            <p className="text-sm text-amber-800 mt-0.5">
              {file.sexAnalysisBlockedReason ||
                'The co-maker field is universally blank across the evaluated population, so single-vs-joint cannot be determined. Ethnicity analysis is unaffected.'}
            </p>
            <p className="text-sm text-amber-800 mt-2">
              If the institution confirms its consumer loans have no co-borrowers, you can run the sex analysis on
              primary borrowers only.
            </p>
            <button
              type="button"
              onClick={() => setBorrowerOnly(true)}
              disabled={borrowerOnlyMutation.isPending || runMutation.isPending}
              className="mt-2 inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:opacity-50"
            >
              {borrowerOnlyMutation.isPending ? 'Saving…' : 'Run on primary borrowers only'}
            </button>
          </div>
        </div>
      )}
      {file.sexAnalysisBlocked && file.sexBorrowerOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <InformationCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900">Sex analysis run on primary borrowers only</p>
            <p className="text-sm text-blue-800 mt-0.5">
              No loan in the evaluated population has a co-maker, so joint applications cannot be identified. Each
              loan's sex is proxied from the primary borrower's first name. Exports say the same.
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Turned on{file.sexBorrowerOnlyByName ? ` by ${file.sexBorrowerOnlyByName}` : ''}
              {file.sexBorrowerOnlyAt ? ` on ${new Date(file.sexBorrowerOnlyAt).toLocaleDateString()}` : ''}.{' '}
              <button
                type="button"
                onClick={() => setBorrowerOnly(false)}
                disabled={borrowerOnlyMutation.isPending || runMutation.isPending}
                className="underline hover:text-blue-900 disabled:opacity-50"
              >
                Turn off
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Consumer rate-disparity analysis */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-navy-900 inline-flex items-center gap-2">
          <ChartBarIcon className="h-6 w-6 text-ocean-500" />
          Consumer Rate Disparity
        </h2>
        <div className="flex items-center gap-2">
          {analysis && (
            <>
              <button
                onClick={() => exportMutation.mutate('xlsx')}
                disabled={exportMutation.isPending}
                title="Download an examiner-style Excel report of this analysis"
                className="inline-flex items-center gap-2 rounded-lg border border-ocean-300 text-ocean-700 bg-white px-4 py-2 text-sm font-semibold hover:bg-ocean-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingFormat === 'xlsx' ? (
                  <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Exporting…</>
                ) : (
                  <><ArrowDownTrayIcon className="h-4 w-4" /> Excel</>
                )}
              </button>
              <button
                onClick={() => exportMutation.mutate('pdf')}
                disabled={exportMutation.isPending}
                title="Download an examiner-style PDF report of this analysis"
                className="inline-flex items-center gap-2 rounded-lg border border-ocean-300 text-ocean-700 bg-white px-4 py-2 text-sm font-semibold hover:bg-ocean-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {exportingFormat === 'pdf' ? (
                  <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Exporting…</>
                ) : (
                  <><ArrowDownTrayIcon className="h-4 w-4" /> PDF</>
                )}
              </button>
            </>
          )}
          <button
            onClick={() => setCodeMapOpen(true)}
            title="Manage your bank's collateral / type / note-purpose code descriptions"
            className="relative inline-flex items-center gap-2 rounded-lg border border-gray-200 text-gray-700 bg-white px-4 py-2 text-sm font-semibold hover:bg-gray-50 transition-colors"
          >
            <TableCellsIcon className="h-4 w-4" /> Manage codes
            {totalUnmapped > 0 && (
              <span className="ml-1 inline-flex items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-white">
                {totalUnmapped}
              </span>
            )}
          </button>
          <button
            onClick={() => runMutation.mutate()}
            disabled={runMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-ocean-500 text-white px-4 py-2 text-sm font-semibold hover:bg-ocean-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {runMutation.isPending ? (
              <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Running…</>
            ) : (
              analysis ? 'Re-run analysis' : 'Run analysis'
            )}
          </button>
        </div>
      </div>

      {/* Unmapped-codes prompt — Michael's low-friction flow: surface codes this file uses that
          the bank's map doesn't decode yet, one click from filling them in. */}
      {totalUnmapped > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <div className="flex items-start gap-2 text-amber-900">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
            <span>
              <b>{totalUnmapped}</b> code{totalUnmapped === 1 ? '' : 's'} in this file {totalUnmapped === 1 ? 'is' : 'are'} showing
              as raw numbers. Add descriptions so the analysis and exports read in plain English. This is
              display-only — it won't change any numbers.
            </span>
          </div>
          <button
            onClick={() => setCodeMapOpen(true)}
            className="flex-shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-600"
          >
            Add descriptions
          </button>
        </div>
      )}

      <AlertCodeMapModal isOpen={codeMapOpen} onClose={() => setCodeMapOpen(false)} prefill={prefillCodes} />

      {/* How to read the result cards */}
      <details className="mb-4 bg-ocean-50 border border-ocean-200 rounded-lg px-4 py-3 text-sm">
        <summary className="cursor-pointer font-medium text-navy-900">How to read these numbers</summary>
        <ul className="mt-2 space-y-1.5 text-navy-700 list-none">
          <li><b>+N bps</b> — gap between the group's mean rate and the control's, in basis points (1 bp = 0.01%). Positive = the group pays <i>more</i>.</li>
          <li><b>mean (n=…)</b> — average interest rate for that group; <b>n</b> = loans usable in the comparison.</li>
          <li><b>Significance</b> — shown as a band (same as FLD/HMDA): <b>&lt;1%</b> / <b>&lt;5%</b> = significant (p &lt; 0.05) · <b>&lt;10%</b> = marginal · <b>&gt;10%</b> = not significant · <b>NA</b> = too few loans to test. Exact p is under each card's <i>Statistical detail</i>.</li>
          <li><b>95% CI</b> (under <i>Statistical detail</i>) — the range the true gap likely falls in; if it <b>crosses 0</b> the difference isn't distinguishable from zero.</li>
          <li><b>Risk</b> — only applies when the protected group pays <i>more</i>: <span className="text-red-600 font-semibold">&lt;1% / &lt;5% = HIGH</span> · <span className="text-amber-600 font-semibold">&lt;10% = MEDIUM</span> · <span className="text-green-700 font-semibold">&gt;10% = LOW</span>. If the protected group pays the same or <i>less</i>, risk = <span className="text-gray-600 font-semibold">NA</span> (no disparity toward the group).</li>
          <li><b>"X of Y excluded"</b> — the transparent denominator (JOINT loans under FRB, names that couldn't be proxied, etc.).</li>
        </ul>
      </details>

      {analysisLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
        </div>
      ) : !analysis ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-10 text-center">
          <p className="text-gray-500">No analysis run yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Run the consumer rate-disparity analysis over the {file.consumerLoans?.toLocaleString()} consumer loans in the window.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Summary of Risks — headline of the statistically significant ADVERSE disparities,
              mirroring the HMDA / Fair Lending Dashboard summary. */}
          <div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
              Summary of Risks
            </h3>
            {riskRows.length === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                No statistically significant adverse disparities detected in the consumer population.
                Overall risk: <b>{analysis.overallRiskLevel}</b>.
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Aspect</th>
                      <th className="text-left px-4 py-2 font-medium">Risk factor</th>
                      <th className="text-left px-4 py-2 font-medium">Product</th>
                      <th className="text-left px-4 py-2 font-medium">Significance</th>
                      <th className="text-left px-4 py-2 font-medium">PBG</th>
                      <th className="text-right px-4 py-2 font-medium">Disparity</th>
                      <th className="text-right px-4 py-2 font-medium">Volume</th>
                      <th className="text-left px-4 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {riskRows.map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-gray-700">Pricing</td>
                        <td className="px-4 py-2 font-medium text-navy-900">
                          Rate disparity ({row.category === 'sex' ? 'Sex' : 'Ethnicity'})
                        </td>
                        <td className="px-4 py-2 text-gray-700">{row.product}</td>
                        <td className="px-4 py-2"><SignificanceBadge level={row.band} /></td>
                        <td className="px-4 py-2 text-gray-700">{row.pbg}</td>
                        <td className="px-4 py-2 text-right font-semibold text-red-600">
                          +{row.bps.toFixed(0)} bps
                        </td>
                        <td className="px-4 py-2 text-right text-gray-600">{row.volume.toLocaleString()}</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            {actionFor(row.band)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {sexResults.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Sex</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sexResults.map((r, i) => <ResultCard key={`sex-${i}`} r={r} />)}
              </div>
            </div>
          )}
          {ethResults.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Ethnicity</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ethResults.map((r, i) => <ResultCard key={`eth-${i}`} r={r} />)}
              </div>
            </div>
          )}
          {results.length === 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
              No comparable groups in the analysis population.
            </div>
          )}

          {/* By Call Report Code (6a/6b/6c/6d) — the same sex/ethnicity disparity WITHIN each
              consumer subcode. Collapsible to keep the headline view clean. */}
          {subcodeGroups.length > 0 && (
            <details open className="border-t border-gray-200 pt-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-600 uppercase tracking-wide select-none">
                By call report code (6a / 6b / 6c / 6d)
              </summary>
              <p className="text-xs text-gray-400 mt-1 mb-3">
                The same disparity test run <i>within</i> each consumer subcode. Small per-subcode
                populations often read <b>NA</b> (too few loans to test) — that's expected.
              </p>
              <div className="space-y-5">
                {subcodeGroups.map(([subcode, group]) => {
                  const collMap = productTree.get(subcode);
                  return (
                    <div key={subcode}>
                      <h4 className="text-sm font-medium text-navy-900 mb-2">{subcode}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {group.map((r, i) => <ResultCard key={`${subcode}-${i}`} r={r} />)}
                      </div>

                      {/* Nested "like product" layer: collateral code › loan type. Collapsed by
                          default. Only combinations present in the data appear. */}
                      {collMap && collMap.size > 0 && (
                        <details className="mt-3 ml-1 border-l-2 border-gray-100 pl-3">
                          <summary className="cursor-pointer text-xs font-medium text-ocean-600 hover:text-ocean-700 select-none">
                            By product — collateral code › type ({collMap.size} collateral{collMap.size === 1 ? '' : 's'})
                          </summary>
                          <div className="mt-2 space-y-4">
                            {sortedEntries(collMap).map(([coll, typeMap]) => (
                              <div key={coll}>
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                  Collateral: <span className="font-mono normal-case text-gray-700">{decodeColl(coll)}</span>
                                  {decodeColl(coll) !== coll && (
                                    <span className="ml-1 font-mono text-[10px] text-gray-400">({coll})</span>
                                  )}
                                </p>
                                <div className="mt-1.5 space-y-3">
                                  {sortedEntries(typeMap).map(([typ, rs]) => (
                                    <div key={typ} className="ml-2">
                                      <p className="text-xs text-gray-500">
                                        Type: <span className="text-gray-700">{decodeType(typ)}</span>
                                        {decodeType(typ) !== typ && (
                                          <span className="ml-1 font-mono text-[10px] text-gray-400">({typ})</span>
                                        )}
                                      </p>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
                                        {rs.map((r, i) => <ResultCard key={`${coll}-${typ}-${i}`} r={r} />)}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })}
              </div>
            </details>
          )}

          <p className="text-xs text-gray-400">
            Analyzed {analysis.totalApplications?.toLocaleString()} consumer loans · overall risk{' '}
            <span className="font-medium">{analysis.overallRiskLevel}</span>
            {stratifiedCount > 0 && <> · {stratifiedCount} stratified (call-report-code / product / collateral / type / credit-band) findings persisted to the fair-lending record</>}.
            These findings flow into exam packages and board reports automatically.
          </p>
        </div>
      )}

      {/* Commercial & Agricultural distribution — volume/distribution breakdown, below the
          consumer rate-disparity results (Bobby 2026-06-04). */}
      <AlertLoanDistributionSection fileId={id} />
    </div>
  );
}
